import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActiveBinListItem,
  BinListItem,
} from "../../../@types/DRO/GetAllBinsResponse";
import { ProductListArray } from "../../../@types/DRO/GetAllProductResponse";
import { GetAllProductListApi } from "../../../api/GetProductList/GetAllProductsListApi";
import { WarehouseBinsApi } from "../../../api/WarehouseBins/WarehouseBinsApi";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";

type BinStatus = "Active" | "Inactive";

interface WarehouseBinRow {
  id: number;
  binName: string;
  partsCount: number;
  createdAt: string;
  city: string;
  status: BinStatus;
}

interface DefaultWarehouseOption {
  id: number;
  name: string;
}

interface ActiveBinOption {
  id: number;
  binName: string;
  warehouseName: string | null;
}

interface CreateBinProductRow {
  id: number;
  productId: number;
  productName: string;
  productCode: string;
  referenceCode: string;
  quantity: number;
}

interface CreateBinFormState {
  warehouseId: string;
  binName: string;
  productId: string;
  productCode: string;
  referenceCode: string;
  quantity: string;
}

const initialCreateForm: CreateBinFormState = {
  warehouseId: "",
  binName: "",
  productId: "",
  productCode: "",
  referenceCode: "",
  quantity: "",
};

const ACTIVE_BINS_PAGE_SIZE = 8;

const WarehouseBinsPage = () => {
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [rows, setRows] = useState<WarehouseBinRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [defaultWarehouseLabel, setDefaultWarehouseLabel] = useState("-");
  const [defaultWarehouse, setDefaultWarehouse] = useState<DefaultWarehouseOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [productOptions, setProductOptions] = useState<ProductListArray[]>([]);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [isCreatingBin, setIsCreatingBin] = useState(false);
  const [createBinMessage, setCreateBinMessage] = useState<string | null>(null);
  const [createBinError, setCreateBinError] = useState<string | null>(null);
  const [listRefreshTrigger, setListRefreshTrigger] = useState(0);
  const [togglingRows, setTogglingRows] = useState<Set<number>>(new Set());
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [pendingDeactivateRow, setPendingDeactivateRow] = useState<WarehouseBinRow | null>(null);
  const [activeBins, setActiveBins] = useState<ActiveBinOption[]>([]);
  const [isActiveBinsLoading, setIsActiveBinsLoading] = useState(false);
  const [isActiveBinsSearching, setIsActiveBinsSearching] = useState(false);
  const [isActiveBinsLoadingMore, setIsActiveBinsLoadingMore] = useState(false);
  const [activeBinsPage, setActiveBinsPage] = useState(0);
  const [activeBinsTotalPages, setActiveBinsTotalPages] = useState(1);
  const [activeBinsError, setActiveBinsError] = useState<string | null>(null);
  const [activeBinsSearchQuery, setActiveBinsSearchQuery] = useState("");
  const [debouncedActiveBinsSearch, setDebouncedActiveBinsSearch] = useState("");
  const [selectedTransferBinId, setSelectedTransferBinId] = useState("");
  const [createForm, setCreateForm] = useState<CreateBinFormState>(initialCreateForm);
  const [addedProducts, setAddedProducts] = useState<CreateBinProductRow[]>([]);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isUploadingBulk, setIsUploadingBulk] = useState(false);
  const [bulkUploadError, setBulkUploadError] = useState<string | null>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);
  const activeBinsFetchIdRef = useRef(0);
  const hasActiveBinsLoadedRef = useRef(false);
  const activeBinsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!successToast) return;
    const timer = setTimeout(() => setSuccessToast(null), 2500);
    return () => clearTimeout(timer);
  }, [successToast]);

  useEffect(() => {
    if (!createBinMessage) return;
    const timer = setTimeout(() => setCreateBinMessage(null), 2500);
    return () => clearTimeout(timer);
  }, [createBinMessage]);

  const handleDownloadCreateBinTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      const blob = await WarehouseBinsApi.api.getBinsSampleSheetCreateBin();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "create-bin-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setBulkUploadError("Failed to download template.");
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleBulkFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingBulk(true);
    setBulkUploadError(null);
    try {
      const response = await WarehouseBinsApi.api.uploadBulkBins(file);

      if (response.success) {
        setIsBulkUploadOpen(false);
        setBulkUploadError(null);
        setSuccessToast(response.message || "Bins Uploaded!");
      } else if (response.hasErrorFile) {
        setBulkUploadError(response.message || "Some records could not be processed. Check the downloaded error file.");
      }

      setListRefreshTrigger((prev) => prev + 1);
    } catch {
      setBulkUploadError("Failed to upload file. Please try again.");
    } finally {
      setIsUploadingBulk(false);
      if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
    }
  };

  const normalizedSearch = searchQuery.trim();

  useEffect(() => {
    const fetchRows = async () => {
      setIsLoading(true);
      try {
        const response = await WarehouseBinsApi.api.getAllBinsList(
          currentPage,
          rowsPerPage,
          normalizedSearch,
          statusFilter === "All" ? undefined : statusFilter.toLowerCase()
        );

        setDefaultWarehouseLabel(
          response.data.defaultWarehouseId !== undefined && response.data.defaultWarehouseId !== null
            ? String(response.data.defaultWarehouseId)
            : "-"
        );

        setDefaultWarehouse(
          response.data.defaultWarehouseId && response.data.defaultWarehouseName
            ? {
                id: response.data.defaultWarehouseId,
                name: response.data.defaultWarehouseName,
              }
            : null
        );

        const mappedRows: WarehouseBinRow[] = response.data.data.map(
          (item: BinListItem) => ({
            id: item.id,
            binName: item.binName,
            partsCount: item.partsCount ?? 0,
            createdAt: item.createdAt ?? "-",
            city: item.city ?? item.warehouseName ?? "-",
            status: (item.active ?? item.status) ? "Active" : "Inactive",
          })
        );

        setRows(mappedRows);
        setTotalPages(response.data.totalPages || 1);
      } catch (error) {
        console.error("Error fetching warehouse bins", error);
        setRows([]);
        setTotalPages(1);
        setDefaultWarehouseLabel("-");
        setDefaultWarehouse(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRows();
  }, [currentPage, rowsPerPage, normalizedSearch, listRefreshTrigger, statusFilter]);

  useEffect(() => {
    if (!isCreateMode) {
      return;
    }

    if (defaultWarehouse) {
      setCreateForm((prev) => ({
        ...prev,
        warehouseId: String(defaultWarehouse.id),
        productId: "",
        productCode: "",
        referenceCode: "",
        quantity: "",
      }));
      setAddedProducts([]);
    }
  }, [defaultWarehouse, isCreateMode]);

  useEffect(() => {
    if (!isCreateMode || !createForm.warehouseId) {
      setProductOptions([]);
      setCreateForm((prev) => ({ ...prev, productId: "" }));
      return;
    }

    const fetchProducts = async () => {
      setIsProductsLoading(true);
      try {
        const response = await GetAllProductListApi.api.getAllProductsList(
          Number(createForm.warehouseId)
        );
        setProductOptions(response.data ?? []);
      } catch (error) {
        console.error("Error fetching product options", error);
        setProductOptions([]);
      } finally {
        setIsProductsLoading(false);
      }
    };

    fetchProducts();
  }, [createForm.warehouseId, isCreateMode]);

  useEffect(() => {
    const handler = setTimeout(
      () => setDebouncedActiveBinsSearch(activeBinsSearchQuery.trim()),
      300
    );
    return () => clearTimeout(handler);
  }, [activeBinsSearchQuery]);

  useEffect(() => {
    if (!isDeactivateModalOpen) {
      return;
    }

    const fetchId = ++activeBinsFetchIdRef.current;
    const isInitialFetch = !hasActiveBinsLoadedRef.current;

    if (isInitialFetch) {
      setIsActiveBinsLoading(true);
      setActiveBinsError(null);
      setActiveBins([]);
      setActiveBinsPage(0);
      setActiveBinsTotalPages(1);
    } else {
      setIsActiveBinsSearching(true);
    }

    const fetchActiveBins = async () => {
      try {
        const response = await WarehouseBinsApi.api.getAllActiveBinsList(
          0,
          ACTIVE_BINS_PAGE_SIZE,
          debouncedActiveBinsSearch || undefined
        );
        if (fetchId !== activeBinsFetchIdRef.current) {
          return;
        }

        const mappedBins: ActiveBinOption[] = (response.data.data ?? []).map(
          (item: ActiveBinListItem) => ({
            id: item.id,
            binName: item.binName,
            warehouseName: item.warehouseName ?? null,
          })
        );

        hasActiveBinsLoadedRef.current = true;
        setActiveBinsPage(response.data.pageNumber ?? 0);
        setActiveBinsTotalPages(response.data.totalPages || 1);
        setActiveBins(mappedBins);

        setSelectedTransferBinId((prev) => {
          if (!prev) {
            return prev;
          }
          const pendingId = pendingDeactivateRow?.id;
          const isStillAvailable = mappedBins.some(
            (bin) => bin.id !== pendingId && String(bin.id) === prev
          );
          return isStillAvailable ? prev : "";
        });
      } catch (error: any) {
        if (fetchId !== activeBinsFetchIdRef.current) {
          return;
        }
        console.error("Error fetching active bins", error);
        if (isInitialFetch) {
          setActiveBins([]);
          setActiveBinsPage(0);
          setActiveBinsTotalPages(1);
        }
        setActiveBinsError(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to load active bins. Please try again."
        );
      } finally {
        if (fetchId === activeBinsFetchIdRef.current) {
          setIsActiveBinsLoading(false);
          setIsActiveBinsSearching(false);
        }
      }
    };

    void fetchActiveBins();
  }, [isDeactivateModalOpen, debouncedActiveBinsSearch, pendingDeactivateRow?.id]);

  const filteredRows = rows;

  const transferableActiveBins = useMemo(
    () => activeBins.filter((item) => item.id !== pendingDeactivateRow?.id),
    [activeBins, pendingDeactivateRow]
  );

  const loadMoreActiveBins = useCallback(async () => {
    if (
      isActiveBinsLoading ||
      isActiveBinsSearching ||
      isActiveBinsLoadingMore ||
      activeBinsPage >= activeBinsTotalPages - 1
    ) {
      return;
    }

    const nextPage = activeBinsPage + 1;

    try {
      setIsActiveBinsLoadingMore(true);
      setActiveBinsError(null);

      const response = await WarehouseBinsApi.api.getAllActiveBinsList(
        nextPage,
        ACTIVE_BINS_PAGE_SIZE,
        debouncedActiveBinsSearch || undefined
      );
      const mappedBins: ActiveBinOption[] = (response.data.data ?? []).map(
        (item: ActiveBinListItem) => ({
          id: item.id,
          binName: item.binName,
          warehouseName: item.warehouseName ?? null,
        })
      );

      setActiveBinsPage(response.data.pageNumber ?? nextPage);
      setActiveBinsTotalPages(response.data.totalPages || 1);
      setActiveBins((prev) => [...prev, ...mappedBins]);
    } catch (error: any) {
      console.error("Error fetching more active bins", error);
      setActiveBinsError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to load more active bins. Please try again."
      );
    } finally {
      setIsActiveBinsLoadingMore(false);
    }
  }, [
    activeBinsPage,
    activeBinsTotalPages,
    isActiveBinsLoading,
    isActiveBinsSearching,
    isActiveBinsLoadingMore,
    debouncedActiveBinsSearch,
  ]);

  useEffect(() => {
    if (!isDeactivateModalOpen || isActiveBinsLoading || isActiveBinsSearching || isActiveBinsLoadingMore) {
      return;
    }
    if (activeBinsPage >= activeBinsTotalPages - 1 || transferableActiveBins.length === 0) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      const node = activeBinsListRef.current;
      if (!node) {
        return;
      }
      if (node.scrollHeight <= node.clientHeight + 12) {
        void loadMoreActiveBins();
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [
    isDeactivateModalOpen,
    activeBins,
    transferableActiveBins.length,
    isActiveBinsLoading,
    isActiveBinsSearching,
    isActiveBinsLoadingMore,
    activeBinsPage,
    activeBinsTotalPages,
    loadMoreActiveBins,
  ]);

  const selectedProduct = useMemo(
    () => productOptions.find((product) => String(product.productId) === createForm.productId) ?? null,
    [createForm.productId, productOptions]
  );

  const handleCreateInputChange = (field: keyof CreateBinFormState, value: string) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleWarehouseChange = (warehouseId: string) => {
    setCreateForm((prev) => ({
      ...prev,
      warehouseId,
      productId: "",
      productCode: "",
      referenceCode: "",
      quantity: "",
    }));
    setAddedProducts([]);
    setCreateBinError(null);
    setCreateBinMessage(null);
  };

  const closeDeactivateModal = () => {
    setIsDeactivateModalOpen(false);
    setPendingDeactivateRow(null);
    setSelectedTransferBinId("");
    setActiveBins([]);
    setActiveBinsPage(0);
    setActiveBinsTotalPages(1);
    setActiveBinsError(null);
    setActiveBinsSearchQuery("");
    setDebouncedActiveBinsSearch("");
    setIsActiveBinsSearching(false);
    setIsActiveBinsLoadingMore(false);
    activeBinsFetchIdRef.current += 1;
    hasActiveBinsLoadedRef.current = false;
  };

  const updateBinStatus = async (
    row: WarehouseBinRow,
    active: boolean,
    transferBinId: number | null
  ) => {
    if (togglingRows.has(row.id)) return;

    setTogglingRows((prev) => new Set(prev).add(row.id));
    try {
      await WarehouseBinsApi.api.updateBin(row.id, {
        name: row.binName,
        active,
        transferBinId,
      });
      setSuccessToast(active ? "Bin Marked Active!" : "Bin Marked Inactive!");
      if (!active) {
        closeDeactivateModal();
      }
      setListRefreshTrigger((prev) => prev + 1);
    } catch (error: any) {
      console.error("Error updating bin status", error);
      const serverMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update bin status. Please try again.";

      if (!active) {
        setActiveBinsError(serverMessage);
      }
    } finally {
      setTogglingRows((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const handleProductChange = (productId: string) => {
    const product = productOptions.find((item) => String(item.productId) === productId);

    setCreateForm((prev) => ({
      ...prev,
      productId,
      productCode: product ? String(product.productId) : prev.productCode,
      referenceCode: product ? product.locationName : prev.referenceCode,
    }));
  };

  const handleAddProduct = () => {
    if (!selectedProduct || !createForm.quantity.trim()) {
      return;
    }

    setAddedProducts((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        productId: selectedProduct.productId,
        productName: selectedProduct.productName,
        productCode: createForm.productCode.trim(),
        referenceCode: createForm.referenceCode.trim(),
        quantity: Number(createForm.quantity),
      },
    ]);

    setCreateForm((prev) => ({
      ...prev,
      productId: "",
      productCode: "",
      referenceCode: "",
      quantity: "",
    }));
  };

  const handleToggleStatus = (row: WarehouseBinRow) => {
    if (togglingRows.has(row.id)) return;

    if (row.status === "Active") {
      // If bin has zero parts, deactivate immediately without opening modal
      if ((row.partsCount ?? 0) === 0) {
        void updateBinStatus(row, false, null);
        return;
      }

      setPendingDeactivateRow(row);
      setSelectedTransferBinId("");
      setActiveBinsError(null);
      setActiveBinsSearchQuery("");
      setDebouncedActiveBinsSearch("");
      setIsActiveBinsSearching(false);
      setIsActiveBinsLoadingMore(false);
      activeBinsFetchIdRef.current += 1;
      hasActiveBinsLoadedRef.current = false;
      setIsDeactivateModalOpen(true);
      return;
    }

    updateBinStatus(row, true, null);
  };

  const handleConfirmDeactivate = async () => {
    if (!pendingDeactivateRow) {
      return;
    }

    const needsTransferBin = pendingDeactivateRow.partsCount > 0;
    const transferBinId =
      needsTransferBin && selectedTransferBinId ? Number(selectedTransferBinId) : null;

    if (needsTransferBin && !selectedTransferBinId) {
      setActiveBinsError("Please select an active bin to transfer parts.");
      return;
    }

    await updateBinStatus(pendingDeactivateRow, false, transferBinId);
  };

  const handleActiveBinsScroll = (event: React.SyntheticEvent<HTMLDivElement>) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;

    if (listboxNode.scrollHeight - position <= 12) {
      void loadMoreActiveBins();
    }
  };

  const handleCreateBin = async () => {
    const trimmedBinName = createForm.binName.trim();

    if (!createForm.warehouseId) {
      setCreateBinError("Please select a warehouse.");
      setCreateBinMessage(null);
      return;
    }

    if (!trimmedBinName) {
      setCreateBinError("Please enter a bin name.");
      setCreateBinMessage(null);
      return;
    }

    try {
      setIsCreatingBin(true);
      setCreateBinError(null);
      setCreateBinMessage(null);

      const response = await WarehouseBinsApi.api.addBin({
        name: trimmedBinName,
        warehouseId: Number(createForm.warehouseId),
        active: true,
      });

      setCreateBinMessage(response.message || "Bin created successfully.");
      setSuccessToast("Bin Created!");
      setCreateForm(initialCreateForm);
      setAddedProducts([]);
      setIsCreateMode(false);
      setCurrentPage(0);
      setListRefreshTrigger((prev) => prev + 1);
    } catch (error: any) {
      console.error("Error creating bin", error);
      const serverMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to create bin. Please try again.";
      setCreateBinError(serverMessage);
    } finally {
      setIsCreatingBin(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-64px)] bg-slate-50">
      <div className="hidden w-64 shrink-0 bg-white shadow-xl lg:block">
        <SideBarNavigation />
      </div>

      <main className="flex-1 px-4 py-5 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-[1320px]">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              Warehouse Bins
            </h1>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => { setIsBulkUploadOpen(true); setBulkUploadError(null); }}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0L8 8m4-4l4 4" />
                </svg>
                Bulk Upload
              </button>
            </div>
          </div>
          <p className="mb-4 text-left text-[11px] font-medium text-slate-400">
            Dashboard &gt;{" "}
            {isCreateMode ? (
              <>
                <button
                  type="button"
                  onClick={() => setIsCreateMode(false)}
                  className="cursor-pointer hover:text-slate-600">
                  Warehouse Bins
                </button>
                {" "}&gt; <span className="text-slate-600">Create Bin</span>
              </>
            ) : (
              <span className="text-slate-600">Warehouse Bins</span>
            )}
          </p>

          {isCreateMode ? (
            <section className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-[0_1px_8px_rgba(15,23,42,0.05)]">

              <div className="px-6 py-5">
                <div className="grid gap-6 lg:grid-cols-[1.25fr_1.25fr_auto] lg:items-end">
                  <label className="block">
                    <span className="mb-2 block text-left text-[11px] font-medium text-slate-400">Warehouse</span>
                    <select
                      value={createForm.warehouseId}
                      onChange={(event) => handleWarehouseChange(event.target.value)}
                      disabled={!defaultWarehouse}
                      className="h-10 w-full border-0 border-b border-slate-200 bg-transparent px-0 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-0">
                      <option value={defaultWarehouse ? String(defaultWarehouse.id) : ""}>
                        {defaultWarehouse ? defaultWarehouse.name : "No default warehouse"}
                      </option>
                    </select>
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-left text-[11px] font-medium text-slate-400">Bin Name</span>
                    <input
                      value={createForm.binName}
                      onChange={(event) => handleCreateInputChange("binName", event.target.value)}
                      placeholder="Enter Name"
                      className="h-10 w-full border-0 border-b border-slate-200 bg-transparent px-0 text-sm text-slate-800 outline-none transition placeholder:text-slate-300 focus:border-blue-500 focus:ring-0"
                    />
                  </label>

                  <button
                    type="button"
                    onClick={handleCreateBin}
                    disabled={isCreatingBin || !createForm.warehouseId || !createForm.binName.trim()}
                    className="inline-flex h-10 items-center justify-center rounded-full bg-blue-600 px-8 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300">
                    {isCreatingBin ? "Creating..." : "Create Bin"}
                  </button>
                </div>

                {(createBinMessage || createBinError) && (
                  <p
                    className={`mt-3 text-sm font-medium ${
                      createBinError ? "text-rose-600" : "text-emerald-600"
                    }`}>
                    {createBinError || createBinMessage}
                  </p>
                )}
                </div>
              </section>
          ) : (
            <section className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-[0_1px_8px_rgba(15,23,42,0.05)]">
              <div className="text-left border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">
                Warehouse ID - {defaultWarehouseLabel}
                {defaultWarehouse?.name ? (
                  <span className="ml-2 text-sm font-normal text-slate-500">({defaultWarehouse.name})</span>
                ) : null}
              </div>

              <div className="flex flex-col gap-3 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <label className="text-sm font-semibold text-slate-700" htmlFor="status-filter">
                    Status:
                  </label>
                  <div className="relative">
                    <select
                      id="status-filter"
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                      className="h-10 min-w-[120px] appearance-none rounded-xl border border-slate-200 bg-white pl-3 pr-8 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
                      <option value="All">All</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                    <svg
                      className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6" />
                    </svg>
                  </div>
                </div>

                <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                  <div className="relative w-full sm:max-w-[360px]">
                    <svg
                      className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor">
                      <circle cx="11" cy="11" r="7" strokeWidth={2} />
                      <path d="M20 20l-3.5-3.5" strokeWidth={2} strokeLinecap="round" />
                    </svg>
                    <input
                      value={searchQuery}
                      onChange={(event) => {
                        setCurrentPage(0);
                        setSearchQuery(event.target.value);
                      }}
                      placeholder="Search"
                      className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsCreateMode(true)}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m-7-7h14" />
                    </svg>
                    Create Bin
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto border-t border-slate-100">
                <table className="min-w-full border-collapse text-left">
                  <thead className="bg-slate-50">
                    <tr className="text-[11px] text-left font-medium uppercase tracking-wide text-slate-500">
                      <th className="w-14 px-5 py-4">#</th>
                      <th className="px-5 py-4">Bin Name</th>
                      <th className="px-5 py-4">Part&apos;s Count</th>
                      <th className="px-5 py-4">Created At</th>
                      <th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && (
                      <tr>
                        <td colSpan={6} className="px-5 py-16 text-center text-sm text-slate-500">
                          Loading warehouse bins...
                        </td>
                      </tr>
                    )}
                    {filteredRows.map((row, index) => (
                      <tr key={row.id} className="border-t border-slate-100 text-sm text-slate-700">
                        <td className="px-5 py-4">{currentPage * rowsPerPage + index + 1}</td>
                        <td className="px-5 py-4 font-medium text-slate-900">{row.binName}</td>
                        <td className="px-5 py-4">{row.partsCount}</td>
                        <td className="px-5 py-4">{row.createdAt}</td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              row.status === "Active"
                                ? "bg-emerald-50 text-emerald-500"
                                : "bg-rose-50 text-rose-500"
                            }`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <button
                            type="button"
                            onClick={() => handleToggleStatus(row)}
                            disabled={togglingRows.has(row.id)}
                            className="inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={`Toggle bin status, currently ${row.status}`}>
                            <span
                              className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${
                                row.status === "Active" ? "bg-blue-500" : "bg-slate-300"
                              }`}>
                              <span
                                className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
                                  row.status === "Active" ? "translate-x-4" : "translate-x-0"
                                }`}
                              />
                            </span>
                            {/* removed textual True/False label next to toggle */}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!isLoading && filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-5 py-16 text-center text-sm text-slate-500">
                          No warehouse bins found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col items-center justify-end gap-4 border-t border-slate-100 px-5 py-4 sm:flex-row">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                    disabled={currentPage === 0}
                    className="rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      onClick={() => setCurrentPage(pageNumber - 1)}
                      className={`h-7 w-7 rounded-sm text-xs font-semibold ${
                        currentPage === pageNumber - 1
                          ? "bg-blue-600 text-white"
                          : "text-slate-700 hover:bg-slate-100"
                      }`}>
                      {pageNumber}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, Math.max(totalPages - 1, 0)))
                    }
                    disabled={currentPage >= totalPages - 1}
                    className="rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>

      {isBulkUploadOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setIsBulkUploadOpen(false); }}>
          <div className="relative w-full max-w-lg rounded-2xl bg-white px-10 py-10 shadow-2xl">
            <button
              type="button"
              onClick={() => setIsBulkUploadOpen(false)}
              className="absolute right-5 top-5 text-slate-400 transition hover:text-slate-600">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="flex flex-col items-center gap-5">
              <div className="flex h-16 w-16 items-center justify-center">
                <svg className="h-14 w-14 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 2v6h6" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18v-6m0 0l-2 2m2-2l2 2" />
                </svg>
              </div>

              <p className="text-center text-sm text-slate-600">
                Please Download The Template Before<br />Uploading The Files.
              </p>

              {bulkUploadError && (
                <p className="text-sm text-rose-600">{bulkUploadError}</p>
              )}

              <div className="flex w-full items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={handleDownloadCreateBinTemplate}
                  disabled={isDownloadingTemplate}
                  className="inline-flex items-center gap-2 rounded-full border border-blue-600 px-6 py-2.5 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
                  </svg>
                  {isDownloadingTemplate ? "Downloading..." : "Download Template"}
                </button>

                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={handleBulkFileChange}
                />
                <button
                  type="button"
                  onClick={() => bulkFileInputRef.current?.click()}
                  disabled={isUploadingBulk}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0L8 8m4-4l4 4" />
                  </svg>
                  {isUploadingBulk ? "Uploading..." : "Upload File"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {successToast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto overflow-hidden rounded-xl bg-white shadow-2xl w-56">
            <div className="flex flex-col items-center gap-3 px-6 py-8">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500">
                <svg className="h-7 w-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-slate-700">{successToast}</p>
            </div>
          </div>
        </div>
      )}

      {isDeactivateModalOpen && pendingDeactivateRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeDeactivateModal();
            }
          }}>
          <div className="relative w-full max-w-2xl rounded-2xl bg-white px-8 py-8 shadow-2xl">
            <button
              type="button"
              onClick={closeDeactivateModal}
              className="absolute right-5 top-5 text-slate-400 transition hover:text-slate-600">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mb-6">
              <h2 className="text-left text-xl font-bold text-slate-900">Deactivate Bin</h2>
              <p className="mt-2 text-sm text-slate-600">
                Select an active bin to move parts from {pendingDeactivateRow.binName}.
                {pendingDeactivateRow.partsCount === 0
                  ? " This bin has no parts, so transferBinId will be sent as null."
                  : ""}
              </p>
            </div>

            {activeBinsError && (
              <p className="mb-4 text-sm font-medium text-rose-600">{activeBinsError}</p>
            )}

            <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                Active Bins
              </div>

              <div className="relative mb-3">
                <svg
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor">
                  <circle cx="11" cy="11" r="7" strokeWidth={2} />
                  <path d="M20 20l-3.5-3.5" strokeWidth={2} strokeLinecap="round" />
                </svg>
                <input
                  value={activeBinsSearchQuery}
                  onChange={(event) => setActiveBinsSearchQuery(event.target.value)}
                  placeholder="Search active bins"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-10 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
                {isActiveBinsSearching && (
                  <svg
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400"
                    viewBox="0 0 24 24"
                    fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                )}
              </div>

              {isActiveBinsLoading && activeBins.length === 0 ? (
                <p className="text-sm text-slate-500">Loading active bins...</p>
              ) : transferableActiveBins.length > 0 ? (
                <div
                  ref={activeBinsListRef}
                  className={`max-h-60 space-y-2 overflow-y-auto pr-1 transition-opacity ${
                    isActiveBinsSearching ? "pointer-events-none opacity-60" : ""
                  }`}
                  onScroll={handleActiveBinsScroll}>
                  {transferableActiveBins.map((bin) => (
                    <label
                      key={bin.id}
                      className={`flex cursor-pointer items-center justify-between rounded-lg border px-4 py-3 transition ${
                        selectedTransferBinId === String(bin.id)
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 bg-white hover:border-blue-300"
                      }`}>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{bin.binName}</div>
                        <div className="text-xs text-slate-500">
                          {bin.warehouseName ? bin.warehouseName : "No warehouse assigned"}
                        </div>
                      </div>
                      <input
                        type="radio"
                        name="transfer-bin"
                        value={bin.id}
                        checked={selectedTransferBinId === String(bin.id)}
                        onChange={(event) => setSelectedTransferBinId(event.target.value)}
                        className="h-4 w-4 accent-blue-600"
                      />
                    </label>
                  ))}
                </div>
              ) : !isActiveBinsSearching ? (
                <p className="text-sm text-slate-500">
                  {debouncedActiveBinsSearch
                    ? "No active bins match your search."
                    : "No active bins available."}
                </p>
              ) : null}

              {isActiveBinsLoadingMore && (
                <p className="mt-3 text-center text-xs text-slate-500">Loading more active bins...</p>
              )}
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeDeactivateModal}
                className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeactivate}
                disabled={
                  (isActiveBinsLoading && activeBins.length === 0) ||
                  togglingRows.has(pendingDeactivateRow.id) ||
                  (pendingDeactivateRow.partsCount > 0 && !selectedTransferBinId)
                }
                className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300">
                {togglingRows.has(pendingDeactivateRow.id) ? "Saving..." : "Deactivate Bin"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseBinsPage;